import { z } from 'zod';
import {
  APPROVAL_STATUSES,
  HOLIDAY_POLICIES,
  PERMISSIONS,
  PRIORITIES,
  RECURRENCE_FREQS,
  REMINDER_INTENSITIES,
  REMINDER_MODES,
  ROLES,
  SORT_FIELDS,
  TASK_STATUSES,
  TASK_TYPES,
  TASK_VIEWS,
  THEMES,
  VISIBILITIES,
  WAITING_FOR,
} from './enums.js';

export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
  .refine((s) => {
    const [y, m, d] = s.split('-').map(Number) as [number, number, number];
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
  }, 'Not a real calendar date');

export const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be HH:mm (24h)');

export const uuid = z.string().uuid();

const trimmed = (max: number) => z.string().trim().max(max);

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** How a due / follow-up / next-action date is specified. RELATIVE is resolved server-side. */
export const dateSpecSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('NONE') }),
  z.object({ mode: z.literal('DATE'), date: isoDate, time: hhmm.optional().nullable() }),
  z.object({
    mode: z.literal('RELATIVE'),
    amount: z.number().int().min(0).max(3660),
    workingDays: z.boolean().default(false),
    time: hhmm.optional().nullable(),
  }),
]);
export type DateSpec = z.infer<typeof dateSpecSchema>;

// ---------------------------------------------------------------------------
// Reminders
// ---------------------------------------------------------------------------

export const reminderSpecSchema = z
  .object({
    kind: z.enum(['BEFORE_DUE', 'AT_DUE', 'AFTER_DUE', 'ABSOLUTE']),
    offsetMinutes: z
      .number()
      .int()
      .min(0)
      .max(60 * 24 * 366)
      .default(0),
    at: z.string().datetime({ offset: true }).optional().nullable(),
    mode: z.enum(REMINDER_MODES).default('ONCE'),
    intensity: z.enum(REMINDER_INTENSITIES).default('NORMAL'),
    repeatEveryMinutes: z
      .number()
      .int()
      .min(5)
      .max(60 * 24 * 7)
      .optional()
      .nullable(),
  })
  .superRefine((r, ctx) => {
    if (r.kind === 'ABSOLUTE' && !r.at) ctx.addIssue({ code: 'custom', message: 'Absolute reminder needs a time' });
    if (r.mode !== 'ONCE' && !r.repeatEveryMinutes)
      ctx.addIssue({ code: 'custom', message: 'Repeating reminder needs an interval' });
  });
export type ReminderSpec = z.infer<typeof reminderSpecSchema>;

// ---------------------------------------------------------------------------
// Recurrence
// ---------------------------------------------------------------------------

export const recurrenceRuleBaseSchema = z.object({
    freq: z.enum(RECURRENCE_FREQS),
    interval: z.number().int().min(1).max(3650).default(1),
    /** ISO weekday numbers 1=Mon … 7=Sun (WEEKLY with selected weekdays). */
    weekdays: z.array(z.number().int().min(1).max(7)).max(7).optional().nullable(),
    /** Day of month for MONTHLY / YEARLY. Days beyond month end clamp to the last day. */
    monthDay: z.number().int().min(1).max(31).optional().nullable(),
    /** Month for YEARLY (1-12). */
    month: z.number().int().min(1).max(12).optional().nullable(),
    startDate: isoDate,
    endDate: isoDate.optional().nullable(),
    maxOccurrences: z.number().int().min(1).max(100000).optional().nullable(),
    holidayPolicy: z.enum(HOLIDAY_POLICIES).default('NONE'),
    time: hhmm.optional().nullable(),
  });

export const recurrenceRuleSchema = recurrenceRuleBaseSchema.superRefine((r, ctx) => {
    if (r.endDate && r.endDate < r.startDate)
      ctx.addIssue({ code: 'custom', message: 'End date is before start date', path: ['endDate'] });
    if (r.freq === 'WEEKLY' && r.weekdays && r.weekdays.length === 0)
      ctx.addIssue({ code: 'custom', message: 'Pick at least one weekday', path: ['weekdays'] });
  });
export type RecurrenceRule = z.infer<typeof recurrenceRuleSchema>;

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export const followUpSpecSchema = z.object({
  title: trimmed(300).optional().nullable(),
  due: dateSpecSchema,
  assigneeId: uuid.optional().nullable(),
  priority: z.enum(PRIORITIES).optional().nullable(),
});
export type FollowUpSpec = z.infer<typeof followUpSpecSchema>;

const taskFields = {
  title: z.string().trim().min(1, 'Title is required').max(300),
  description: trimmed(10000).optional().nullable(),
  notes: trimmed(20000).optional().nullable(),
  priority: z.enum(PRIORITIES).default('MEDIUM'),
  status: z.enum(['INBOX', 'PLANNED', 'IN_PROGRESS', 'WAITING']).optional(),
  taskType: z.enum(TASK_TYPES).default('FIXED_DATE'),
  visibility: z.enum(VISIBILITIES).default('TEAM'),
  projectId: uuid.optional().nullable(),
  categoryId: uuid.optional().nullable(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  assigneeId: uuid.optional().nullable(),
  due: dateSpecSchema.default({ mode: 'NONE' }),
  reminders: z.array(reminderSpecSchema).max(10).optional(),
  recurrence: recurrenceRuleSchema.optional().nullable(),
  followUp: followUpSpecSchema.optional().nullable(),
  nextAction: trimmed(500).optional().nullable(),
  nextActionDue: dateSpecSchema.optional().nullable(),
  waitingFor: z.enum(WAITING_FOR).optional().nullable(),
  waitingForNote: trimmed(200).optional().nullable(),
  presetId: uuid.optional().nullable(),
};

export const createTaskSchema = z.object(taskFields).superRefine((t, ctx) => {
  if (t.taskType === 'RECURRING' && !t.recurrence)
    ctx.addIssue({ code: 'custom', message: 'Recurring task needs a schedule', path: ['recurrence'] });
  if (t.status === 'WAITING' && !t.waitingFor)
    ctx.addIssue({ code: 'custom', message: 'Choose who you are waiting for', path: ['waitingFor'] });
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = z.object({
  version: z.number().int().min(1),
  title: taskFields.title.optional(),
  description: taskFields.description,
  notes: taskFields.notes,
  priority: z.enum(PRIORITIES).optional(),
  status: z.enum(['INBOX', 'PLANNED', 'IN_PROGRESS', 'WAITING']).optional(),
  taskType: z.enum(TASK_TYPES).optional(),
  visibility: z.enum(VISIBILITIES).optional(),
  projectId: uuid.optional().nullable(),
  categoryId: uuid.optional().nullable(),
  tags: taskFields.tags,
  assigneeId: uuid.optional().nullable(),
  due: dateSpecSchema.optional(),
  reminders: z.array(reminderSpecSchema).max(10).optional(),
  nextAction: taskFields.nextAction,
  nextActionDue: dateSpecSchema.optional().nullable(),
  waitingFor: taskFields.waitingFor,
  waitingForNote: taskFields.waitingForNote,
});
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const snoozeSchema = z.discriminatedUnion('preset', [
  z.object({ preset: z.enum(['15m', '30m', '1h', 'tonight', 'tomorrow', '2d', '7d']) }),
  z.object({ preset: z.literal('custom'), until: z.string().datetime({ offset: true }) }),
]);
export type SnoozeInput = z.infer<typeof snoozeSchema>;
export const SNOOZE_PRESETS = [
  { id: '15m', label: '15 min' },
  { id: '30m', label: '30 min' },
  { id: '1h', label: '1 hour' },
  { id: 'tonight', label: 'Tonight' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: '2d', label: '2 days' },
  { id: '7d', label: '7 days' },
] as const;

export const versionOnlySchema = z.object({ version: z.number().int().min(1) });
export const cancelTaskSchema = z.object({ version: z.number().int().min(1), reason: trimmed(500).optional() });
export const completeTaskSchema = z.object({
  version: z.number().int().min(1),
  followUp: followUpSpecSchema.optional().nullable(),
});
export const addFollowUpSchema = followUpSpecSchema;
export const noteSchema = z.object({ text: z.string().trim().min(1).max(5000) });

export const bulkActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('complete'), taskIds: z.array(uuid).min(1).max(200) }),
  z.object({ action: z.literal('cancel'), taskIds: z.array(uuid).min(1).max(200) }),
  z.object({ action: z.literal('archive'), taskIds: z.array(uuid).min(1).max(200) }),
  z.object({ action: z.literal('snooze'), taskIds: z.array(uuid).min(1).max(200), snooze: snoozeSchema }),
  z.object({ action: z.literal('priority'), taskIds: z.array(uuid).min(1).max(200), priority: z.enum(PRIORITIES) }),
  z.object({ action: z.literal('assign'), taskIds: z.array(uuid).min(1).max(200), assigneeId: uuid.nullable() }),
  z.object({
    action: z.literal('move'),
    taskIds: z.array(uuid).min(1).max(200),
    projectId: uuid.nullable().optional(),
    categoryId: uuid.nullable().optional(),
  }),
  z.object({
    action: z.literal('tag'),
    taskIds: z.array(uuid).min(1).max(200),
    tag: z.string().trim().min(1).max(40),
  }),
]);
export type BulkActionInput = z.infer<typeof bulkActionSchema>;

export const taskListQuerySchema = z.object({
  view: z
    .enum(['all', 'inbox', 'today', 'overdue', 'upcoming', 'followups', 'waiting', 'recurring', 'mine', 'team', 'history'])
    .default('all'),
  q: z.string().trim().max(200).optional(),
  priority: z.array(z.enum(PRIORITIES)).optional(),
  status: z.array(z.enum(TASK_STATUSES)).optional(),
  taskType: z.array(z.enum(TASK_TYPES)).optional(),
  visibility: z.array(z.enum(VISIBILITIES)).optional(),
  assigneeId: z.array(z.union([uuid, z.literal('none')])).optional(),
  createdById: z.array(uuid).optional(),
  projectId: z.array(uuid).optional(),
  categoryId: z.array(uuid).optional(),
  tag: z.array(z.string().max(40)).optional(),
  dueFrom: isoDate.optional(),
  dueTo: isoDate.optional(),
  createdFrom: isoDate.optional(),
  createdTo: isoDate.optional(),
  includeClosed: z.coerce.boolean().optional(),
  sort: z.enum(SORT_FIELDS).default('due'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(200).optional(),
});
export type TaskListQuery = z.infer<typeof taskListQuerySchema>;

export const quickAddParseSchema = z.object({ text: z.string().trim().min(1).max(500) });

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

export const presetConfigSchema = z.object({
  title: trimmed(300).optional().nullable(),
  description: trimmed(5000).optional().nullable(),
  priority: z.enum(PRIORITIES).optional().nullable(),
  taskType: z.enum(TASK_TYPES).optional().nullable(),
  visibility: z.enum(VISIBILITIES).optional().nullable(),
  due: dateSpecSchema.optional().nullable(),
  reminders: z.array(reminderSpecSchema).max(10).optional().nullable(),
  followUp: followUpSpecSchema.optional().nullable(),
  recurrence: recurrenceRuleBaseSchema
    .omit({ startDate: true })
    .extend({ startDate: isoDate.optional() })
    .optional()
    .nullable(),
  projectId: uuid.optional().nullable(),
  categoryId: uuid.optional().nullable(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional().nullable(),
  assigneeId: uuid.optional().nullable(),
  nextAction: trimmed(500).optional().nullable(),
  waitingFor: z.enum(WAITING_FOR).optional().nullable(),
});
export type PresetConfig = z.infer<typeof presetConfigSchema>;

export const presetInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  emoji: z.string().max(8).optional().nullable(),
  scope: z.enum(['PERSONAL', 'GLOBAL']).default('PERSONAL'),
  config: presetConfigSchema,
});
export type PresetInput = z.infer<typeof presetInputSchema>;

// ---------------------------------------------------------------------------
// Holidays & working days
// ---------------------------------------------------------------------------

export const holidayInputSchema = z.object({
  date: isoDate,
  name: z.string().trim().min(1).max(120),
  type: z.enum(['HOLIDAY', 'EXTRA_WORKING_DAY']).default('HOLIDAY'),
});
export type HolidayInput = z.infer<typeof holidayInputSchema>;

/** Working-day configuration. Weekdays use ISO numbering (1=Mon … 7=Sun). */
export const workingRulesSchema = z.object({
  weekdays: z.object({
    1: z.boolean(),
    2: z.boolean(),
    3: z.boolean(),
    4: z.boolean(),
    5: z.boolean(),
    6: z.boolean(),
    7: z.boolean(),
  }),
  /**
   * When enabled, Saturdays are decided by their occurrence within the month (1st…5th)
   * and weekdays[6] is ignored. When disabled, weekdays[6] applies to every Saturday.
   */
  saturdayRule: z.object({
    enabled: z.boolean(),
    first: z.boolean(),
    second: z.boolean(),
    third: z.boolean(),
    fourth: z.boolean(),
    fifth: z.boolean(),
  }),
});
export type WorkingRules = z.infer<typeof workingRulesSchema>;

export const DEFAULT_WORKING_RULES: WorkingRules = {
  weekdays: { 1: true, 2: true, 3: true, 4: true, 5: true, 6: false, 7: false },
  saturdayRule: { enabled: true, first: true, second: false, third: true, fourth: false, fifth: true },
};

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const generalSettingsSchema = z.object({
  orgName: z.string().trim().min(1).max(120),
  timezone: z.string().min(1).max(64),
  defaultDueTime: hhmm,
  defaultReminderTime: hhmm,
  tonightTime: hhmm,
  morningTime: hhmm,
  todayFocusLimit: z.number().int().min(1).max(20),
});
export type GeneralSettings = z.infer<typeof generalSettingsSchema>;

export const notificationSettingsSchema = z.object({
  quietHoursEnabled: z.boolean(),
  quietStart: hhmm,
  quietEnd: hhmm,
  criticalBypassesQuietHours: z.boolean(),
  digestWindowMinutes: z.number().int().min(0).max(60),
  maxRepeatsPerReminder: z.number().int().min(1).max(50),
  overdueNotifications: z.boolean(),
  assignmentNotifications: z.boolean(),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

export const approvalSettingsSchema = z.object({
  enabled: z.boolean(),
  requireDenyReason: z.boolean(),
});
export type ApprovalSettings = z.infer<typeof approvalSettingsSchema>;

export const telegramSettingsSchema = z.object({
  taskControlChatId: z.string().trim().max(32).nullable(),
  taskAuditChatId: z.string().trim().max(32).nullable(),
  taskBoardChatId: z.string().trim().max(32).nullable(),
  approvalChatId: z.string().trim().max(32).nullable(),
  approvalAuditChatId: z.string().trim().max(32).nullable(),
  taskBotEnabled: z.boolean(),
  approvalBotEnabled: z.boolean(),
});
export type TelegramSettings = z.infer<typeof telegramSettingsSchema>;

export const telegramSecretsSchema = z.object({
  taskBotToken: z
    .string()
    .trim()
    .regex(/^\d{5,}:[A-Za-z0-9_-]{30,}$/, 'Not a valid bot token')
    .nullable()
    .optional(),
  approvalBotToken: z
    .string()
    .trim()
    .regex(/^\d{5,}:[A-Za-z0-9_-]{30,}$/, 'Not a valid bot token')
    .nullable()
    .optional(),
});

export const backupSettingsSchema = z.object({
  scheduledEnabled: z.boolean(),
  scheduleTime: hhmm,
  retentionCount: z.number().int().min(1).max(365),
});
export type BackupSettings = z.infer<typeof backupSettingsSchema>;

// ---------------------------------------------------------------------------
// Users / auth
// ---------------------------------------------------------------------------

/**
 * PIN rule: digits only, at least 4 digits, and not all the same digit (1111, 0000 … are rejected).
 * Returns a human message, or null when the PIN is acceptable.
 */
export const PIN_MIN = 4;
export const PIN_MAX = 12;
export function pinProblem(pin: string): string | null {
  if (!/^\d*$/.test(pin)) return 'PIN must contain digits only';
  if (pin.length < PIN_MIN) return `PIN must be at least ${PIN_MIN} digits`;
  if (pin.length > PIN_MAX) return `PIN can be at most ${PIN_MAX} digits`;
  if (/^(\d)\1+$/.test(pin)) return 'PIN digits cannot all be the same';
  return null;
}
export const pinSchema = z.string().superRefine((p, ctx) => {
  const problem = pinProblem(p);
  if (problem) ctx.addIssue({ code: 'custom', message: problem });
});

/** User ID: 3–32 characters, letters/digits/dot/underscore/hyphen, case-insensitive (stored lower-case). */
export const userIdSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'User ID must be at least 3 characters')
  .max(32, 'User ID can be at most 32 characters')
  .regex(/^[a-z0-9][a-z0-9._-]*$/, 'Use letters, digits, dot, underscore or hyphen');

export const loginSchema = z.object({
  userId: z.string().trim().toLowerCase().min(1, 'Enter your User ID').max(64),
  pin: z.string().min(1, 'Enter your PIN').max(PIN_MAX),
});

export const twoFactorLoginSchema = z.object({
  challengeId: z.string().min(10).max(100),
  code: z.string().trim().min(4).max(20),
});

export const forgotPinSchema = z.object({ userId: z.string().trim().toLowerCase().min(1).max(64) });
export const resetPinWithCodeSchema = z.object({
  challengeId: z.string().min(10).max(100),
  code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code'),
  newPin: pinSchema,
});

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(200)
  .optional()
  .nullable()
  .transform((v) => (v ? v : null))
  .refine((v) => v === null || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v), 'Enter a valid email or leave it empty');

export const createUserSchema = z.object({
  name: z.string().trim().min(1).max(100),
  username: userIdSchema,
  email: optionalEmail,
  role: z.enum(ROLES),
  pin: pinSchema,
});
export const updateUserSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  email: optionalEmail,
  role: z.enum(ROLES).optional(),
  isActive: z.boolean().optional(),
  permissionOverrides: z.record(z.enum(PERMISSIONS), z.boolean().nullable()).optional(),
});
export const changePinSchema = z.object({ currentPin: z.string().min(1).max(PIN_MAX), newPin: pinSchema });
export const resetPinSchema = z.object({ newPin: pinSchema });

export const TWO_FACTOR_METHODS = ['NONE', 'TOTP', 'TELEGRAM'] as const;
export type TwoFactorMethod = (typeof TWO_FACTOR_METHODS)[number];
export const confirmPinSchema = z.object({ pin: z.string().min(1).max(PIN_MAX) });
export const enableTotpSchema = z.object({ pin: z.string().min(1).max(PIN_MAX), code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code') });
export const enableTelegram2faSchema = z.object({ pin: z.string().min(1).max(PIN_MAX), code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code') });

/**
 * Clear data (admin only). WORK removes tasks, approvals, notifications and their history but keeps
 * people, settings and lists. EVERYTHING is a factory reset that keeps only the acting admin's account.
 */
export const CLEAR_DATA_SCOPES = ['WORK', 'EVERYTHING'] as const;
export type ClearDataScope = (typeof CLEAR_DATA_SCOPES)[number];
export const CLEAR_DATA_CONFIRM_WORD = 'CLEAR';
export const clearDataSchema = z.object({
  scope: z.enum(CLEAR_DATA_SCOPES),
  pin: z.string().min(1).max(PIN_MAX),
  confirm: z.string(),
});
export type ClearDataInput = z.infer<typeof clearDataSchema>;

export const preferencesSchema = z.object({
  theme: z.enum(THEMES).optional(),
  defaultView: z.enum(TASK_VIEWS).optional(),
  sort: z.object({ field: z.enum(SORT_FIELDS), dir: z.enum(['asc', 'desc']) }).optional(),
});

export const rolePermissionsSchema = z.object({ permissions: z.array(z.enum(PERMISSIONS)) });

// ---------------------------------------------------------------------------
// Saved views / taxonomy
// ---------------------------------------------------------------------------

export const savedViewSchema = z.object({
  name: z.string().trim().min(1).max(60),
  query: taskListQuerySchema.partial(),
  viewMode: z.enum(TASK_VIEWS).default('LIST'),
});

export const taxonomySchema = z.object({
  name: z.string().trim().min(1).max(80),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional()
    .nullable(),
});

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

export const createApprovalSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: trimmed(5000).optional().nullable(),
  reason: z.string().trim().min(1, 'Reason is required').max(2000),
  amount: z
    .string()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, 'Amount must be a number with up to 2 decimals')
    .optional()
    .nullable(),
  requiredBy: isoDate.optional().nullable(),
  priority: z.enum(PRIORITIES).default('MEDIUM'),
  additionalDetails: trimmed(5000).optional().nullable(),
});
export type CreateApprovalInput = z.infer<typeof createApprovalSchema>;

export const approvalDecisionSchema = z.object({
  version: z.number().int().min(1),
  comment: trimmed(2000).optional().nullable(),
});
export const approvalListQuerySchema = z.object({
  status: z.enum(APPROVAL_STATUSES).optional(),
  mine: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().max(200).optional(),
});

// ---------------------------------------------------------------------------
// Audit / history
// ---------------------------------------------------------------------------

export const auditQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  actorId: uuid.optional(),
  action: z.string().max(60).optional(),
  entityType: z.enum(['task', 'approval', 'user', 'settings', 'preset', 'holiday', 'telegram', 'backup', 'system']).optional(),
  entityId: z.string().max(80).optional(),
  source: z.enum(['WEB', 'TELEGRAM', 'SYSTEM', 'API']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(200).optional(),
});
